/**
 * Cookie consent state + application.
 *
 * ePrivacy / RODO: non-essential storage (PostHog product analytics) requires
 * prior opt-in. Sentry error monitoring is treated as strictly necessary and is
 * initialized unconditionally in `main.tsx` (a legal judgment call; see the
 * [LAWYER REVIEW] note in the privacy policy). Strictly-necessary state
 * (`vardic_csrf`, the session cookie, language and theme preferences) never
 * needs consent.
 *
 * Only `analytics` is user-gated today. The state is versioned so a schema bump
 * re-prompts everyone, and shaped to allow adding future toggleable categories.
 */
import { getPostHogInstance, initPostHog } from "../posthog";

export const CONSENT_STORAGE_KEY = "vardic_cookie_consent";
export const CONSENT_VERSION = 1;

export interface ConsentState {
  /** Non-essential product analytics (PostHog). */
  analytics: boolean;
  /** Epoch ms when the choice was recorded. */
  decidedAt: number;
  /** Schema version; bumping it invalidates stored choices. */
  version: number;
}

/**
 * The current consent choice, or `null` when the user has not decided yet
 * (absent, malformed, or a stale schema version — all re-prompt).
 */
export function getConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (
      parsed.version !== CONSENT_VERSION ||
      typeof parsed.analytics !== "boolean"
    ) {
      return null;
    }
    return {
      analytics: parsed.analytics,
      decidedAt: typeof parsed.decidedAt === "number" ? parsed.decidedAt : 0,
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

/** Whether the user has made an explicit, current-version choice. */
export function hasDecided(): boolean {
  return getConsent() !== null;
}

/** Persist a consent choice and return the stored state. */
export function saveConsent(choice: { analytics: boolean }): ConsentState {
  const state: ConsentState = {
    analytics: choice.analytics,
    decidedAt: Date.now(),
    version: CONSENT_VERSION,
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be unavailable (e.g. private mode); the choice simply won't
    // persist and the banner reappears next load. Not fatal.
  }
  return state;
}

/**
 * Apply a consent choice to the analytics SDK. Idempotent: `initPostHog()`
 * self-guards against double init, and opting out is safe even if PostHog was
 * never initialized. Re-granting calls `opt_in_capturing()` after init
 * because posthog-js persists an opt-out flag (localStorage) across reloads;
 * a bare init of an already-initialized instance would otherwise keep
 * capture disabled forever.
 */
export async function applyConsent(state: ConsentState): Promise<void> {
  if (state.analytics) {
    if (import.meta.env.VITE_POSTHOG_KEY) {
      await initPostHog();
      getPostHogInstance()?.opt_in_capturing();
    }
    return;
  }
  getPostHogInstance()?.opt_out_capturing();
}
